import { navigateTo, useRoute } from '#imports'
import { type Ref, onMounted, ref } from 'vue'
import type { LocationQuery } from 'vue-router'

import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'

const DEFAULT_PAGE = 1

type BaseFiltersValues = {
  page?: number
  [key: string]: unknown
}

type Query = LocationQuery | Record<string, number | string>

/**
 * This composable is a all-in-one filter system for pages
 * which aims to sync filters' values with the URL query parameters.
 */
export function useFilters<T extends BaseFiltersValues>(
  initialFiltersValues: T,
) {
  const route = useRoute()

  /**
   * We filter the query to keep only the allowed keys.
   * This is to prevent unwanted query-params to be used by form or sent to API.
   */
  function getSafeQuery(allowedKeys: string[], query: Query): T {
    return allowedKeys.reduce(
      (acc, key) => (key in query ? { ...acc, [key]: query[key] } : acc),
      {} as T,
    )
  }

  const filtersKeys = Object.keys(initialFiltersValues)
  const safeQuery = getSafeQuery(filtersKeys, route.query)

  // TODO: We are using this nasty cast because active filters is typed as UnwrapedRef
  const filtersValues = ref<T>({
    ...initialFiltersValues,
    ...safeQuery,
    // Make sure page is always a number to ease it's consumption with pagination
    page: parseInt(
      safeQuery.page?.toString() ||
        initialFiltersValues.page?.toString() ||
        DEFAULT_PAGE.toString(),
      10,
    ),
  }) as Ref<T>

  const cleanedValues = removeEmptyValuesInObject<T>(filtersValues.value)

  const sanitizedFiltersValues = ref(cleanedValues) as Ref<Partial<T>>

  onMounted(async () => {
    await navigateTo({
      ...route,
      hash: route.hash,
      query: cleanedValues as Query,
      replace: true,
    })
  })

  async function setFilters(values: T) {
    // We keep the empty string values to satisfy form components that needs string
    filtersValues.value = getSafeQuery(filtersKeys, values as Query)

    // We remove the empty string values to send to the API and query params
    const query = removeEmptyValuesInObject(values) as Query
    sanitizedFiltersValues.value = getSafeQuery(filtersKeys, query)

    await navigateTo({
      ...route,
      hash: route.hash,
      query,
    })
  }

  async function resetFilters() {
    await setFilters({ ...initialFiltersValues, page: DEFAULT_PAGE })
  }

  async function submitFilters() {
    await setFilters({ ...filtersValues.value, page: DEFAULT_PAGE })
  }

  async function setCurrentPage(page: number) {
    filtersValues.value.page = page
    await setFilters(filtersValues.value)
  }

  return {
    /**
     * Restore the values to the given initialValues passed as argument
     */
    resetFilters,
    /**
     * Commit the in-memory filters' values used as v-model in the URL
     */
    submitFilters,
    /**
     * Set the the current page value and submit the filters
     */
    setCurrentPage,
    /**
     * The v-model to bind to your form inputs
     */
    filtersValues,
    /*
     * Those values are the ones stored in the URL query-params
     * to be used to send to the API.
     */
    sanitizedFiltersValues,
  }
}
